'use client';

import { Loader2, Search, X } from 'lucide-react';
import { cn } from '@/src/lib/utils/command';
import { Button } from '@/src/components/ui/button';
import { Command, CommandGroup, CommandItem, CommandList } from '@/src/components/ui/command';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchStore } from '@/src/lib/state-stores/searchStore';
import { useDebounce } from '@/src/lib/hooks/use-debounce';
import { createPortal } from 'react-dom';
import { Input } from '../ui/input';
import SearchHistory from './RenderSearchHistory';
import { Link, useRouter } from '@/src/i18n/navigation';
import { Routes } from '@/src/routes/routes';
import Typography from '../ui/typography';
import { useLocale, useTranslations } from 'next-intl';
import { useProducts, useSuggestedIndustries } from '@/src/app/api/dialogApis/searchProducts';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';

// Type Definitions
interface SearchResult {
  id: number;
  integrationId: string;
  index: number | null;
  productIndex: number;
  searchType: number;
  score: number;
  arabicName: string;
  englishName: string;
  arabicDescription: string;
  englishDescription: string;
  branchId: string;
  branchName: string;
  storeId: string;
  storeName: string;
  defaultImage: string;
  price: number;
  originalPrice: number;
  currency: string | null;
  categoryId: string | null;
  categoryIndex: number | null;
  categoryArabicName: string | null;
  categoryEnglishName: string | null;
  isSoldOut: boolean;
  lowestVariantPrice: number | null;
  metadataHolder: unknown | null;
  quickAdd: boolean;
  returnable: boolean;
  tagsIds: number[] | null;
  tax: number;
  taxType: number;
}

interface AsyncAutocompleteProps {
  className?: string;
  placeholder?: string;
  onSelect?: (result: SearchResult) => void;
  debounceMs?: number;
}

const EmptyState = ({ message }: { message: string }) => (
  <div className="text-muted-foreground py-6 text-center text-sm">
    <Typography>{message}</Typography>
  </div>
);

const RenderEmtyStateWithIndustry = ({ setIsOpen }: { setIsOpen: any }) => {
  const t = useTranslations('search');
  const locale = useLocale();
  const { data } = useSuggestedIndustries();
  const industries = data?.flatMap((item) => item?.industries ?? []);

  return (
    <div className="flex flex-col gap-3 p-4">
      <Typography variant={'body1'} color={'text'} className="px-3">
        {t('TryToSearchFor')}
      </Typography>
      <div>
        {industries?.map((industry, idx) => (
          <Link
            key={industry.id || idx}
            href={{
              pathname: Routes.search,
              query: { query: locale === 'ar' ? industry.arabicName : industry.englishName },
            }}
            onClick={() => setIsOpen(false)}
            className="block rounded-lg px-3 py-0.5 hover:bg-gray-100"
          >
            <Typography key={idx} color={'gray'} className="italic">
              {locale === 'ar' ? industry.arabicName : industry.englishName}
            </Typography>
          </Link>
        ))}
      </div>
    </div>
  );
};

const LoadingState = () => {
  const t = useTranslations('dialog');
  return (
    <div className="text-muted-foreground py-6 text-center text-sm">
      <p>{t('Searching')}</p>
    </div>
  );
};

export default function AsyncAutocomplete({
  className,
  placeholder = 'Search...',
  onSelect,
  debounceMs = 300,
}: AsyncAutocompleteProps) {
  // State
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [shouldFetch, setShouldFetch] = useState(true); // New state to control fetching

  // Hooks
  const { searchHistory, addSearch } = useSearchStore();
  const debouncedQuery = useDebounce(query, debounceMs);

  // Only fetch if shouldFetch is true
  const {
    data: results,
    isFetched,
    isLoading,
  } = useProducts(shouldFetch ? debouncedQuery : '', false);

  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('dialog');
  const locale = useLocale();

  // Update options when search results are fetched
  useEffect(() => {
    if (results && isFetched && shouldFetch) {
      setOptions(results);
      trackEvent(AmplitudeEvents.search_performed, {
        search_keyword: query.trim(),
      });
    }
  }, [results, isFetched, shouldFetch]);

  // Dropdown position handling
  const updateDropdownPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Event Handlers
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShouldFetch(true); // Re-enable fetching when user types
    setIsOpen(true);
    if (value.length === 0) {
      setOptions([]);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setIsOpen(true);
    setShouldFetch(true); // Re-enable fetching when focused
  };

  const handleSelect = (result: SearchResult) => {
    setQuery(result.englishName || result.arabicName);
    setIsOpen(false);
    setIsFocused(false);
    onSelect?.(result);
    inputRef.current?.blur();
  };

  const handleSearch = () => {
    if (query.trim()) {
      // Stop fetching immediately
      setShouldFetch(false);

      // Close dropdown and blur input
      setIsOpen(false);
      setIsFocused(false);
      inputRef.current?.blur();

      // Add to search history
      addSearch(query.trim());

      // Navigate immediately
      router.push(`/search?query=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleClear = () => {
    setOptions([]);
    setQuery('');
    setShouldFetch(true); // Re-enable fetching
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      handleSearch(); // This will now immediately stop fetching and navigate
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  // Render conditions
  const showHistory = isOpen && query.length === 0 && searchHistory.length > 0;
  const showResults = isOpen && query.length > 0;
  const showEmptyState = isOpen && query.length === 0 && searchHistory.length === 0;
  const isSearching = shouldFetch && query.length > 0 && (query !== debouncedQuery || isLoading);

  return (
    <div className={cn('relative mt-2 w-full md:mt-0', className)} ref={containerRef}>
      {/* Search Input */}
      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 h-4 w-4 -translate-y-1/2 transform ltr:left-3 rtl:right-3" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={t('placeholder')}
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          className={cn('h-10 rounded-xl ps-10 pe-10 text-base')}
        />
        <div className="absolute top-1/2 flex -translate-y-1/2 transform items-center gap-1 ltr:right-0 rtl:left-0">
          {isSearching && <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />}
          {query && (
            <Button variant="ghost" size="sm" onClick={handleClear}>
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Dropdown Portal */}
      {typeof window !== 'undefined' &&
        isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="absolute max-h-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
            style={{
              top: dropdownPosition.top + 4,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 9999,
            }}
          >
            <Command shouldFilter={false} className="h-full max-h-84">
              <CommandList className="max-h-80 overflow-y-auto pb-4">
                {showHistory && (
                  <CommandGroup>
                    <SearchHistory setOpen={setIsOpen} />
                  </CommandGroup>
                )}
                {showEmptyState && <RenderEmtyStateWithIndustry setIsOpen={setIsOpen} />}
                {showResults && (
                  <>
                    {isSearching ? (
                      <LoadingState />
                    ) : options.length === 0 && debouncedQuery.length > 0 && shouldFetch ? (
                      <EmptyState message={`${t('no-result-found')} "${debouncedQuery}"`} />
                    ) : (
                      <CommandGroup>
                        <div className="border-b bg-gray-50 px-4 py-2 text-xs font-medium text-gray-500">
                          {t('QuickResults')}
                        </div>
                        {options.map((result) => (
                          <div
                            key={result.id}
                            className={`hover:bg-muted w-full cursor-pointer rounded-md transition-colors`}
                            onClick={() => handleSelect(result)}
                          >
                            <Link
                              href={
                                result.integrationId
                                  ? Routes.productDetails(
                                      result.branchId,
                                      result.storeName,
                                      result.integrationId,
                                      result.englishName,
                                    )
                                  : Routes.branchDetails(result.storeId, result.storeName)
                              }
                            >
                              <div className="line-clamp-1 flex items-center gap-0.5 rounded-md px-2 py-2 hover:bg-black/10 hover:text-white">
                                <Search className="mr-2 h-4 w-4 text-gray-400" />
                                <Typography variant={'body1'} truncate>
                                  {locale === 'en' ? result.englishName : result.arabicName}
                                </Typography>
                              </div>
                            </Link>
                          </div>
                        ))}
                      </CommandGroup>
                    )}
                  </>
                )}
              </CommandList>
            </Command>
          </div>,
          document.body,
        )}
    </div>
  );
}
